// Map legacy repo category IDs onto the nearest Troy real-category glyph so
// demo-data categories (bakery, meatdairy, flowers, makers, ...) still
// render a sensible icon when Google Sheets isn't configured.
//
// Consumed by markerSvg.ts (SVG string builder) and CategoryGlyph.tsx
// (React component). Keeping them in sync here makes future drift impossible.
export const LEGACY_ALIASES: Record<string, string> = {
  bakery: 'baked_goods',
  meatdairy: 'dairy_meat_eggs',
  flowers: 'flowers_plants',
  prepared: 'prepared_foods',
  specialty: 'specialty_foods',
  crafts: 'handmade',
  makers: 'handmade',
  seafood: 'dairy_meat_eggs',
  info: 'information',
  store: 'storefront',
  // Prior canonical IDs — sheets / cached payloads / demo data may still
  // use these; they alias to the new canonical names.
  plants: 'flowers_plants',
  animal_products: 'dairy_meat_eggs',
};

// ─────────────────────────────────────────────────────────────
// 40×40 glyph path strings — illustrated-flat Troy style.
// Single source of truth shared by markerSvg.ts (SVG string builder)
// and the React glyph components in primitives/icons/CategoryGlyphs.tsx.
//
// Tokens:
//   `FG` — legible foreground (cream or charcoal) picked per background.
//   `BG` — the sanitized category color itself. Used for cutouts so they
//          read against the cream-colored surrounding glyph mass.
// Both are substituted at render time.
// ─────────────────────────────────────────────────────────────

// Universal market-stall awning icon — used for all vendor markers.
// Scaled to fit comfortably within the r=17 marker circle.
export const VENDOR_GLYPH =
  '<path d="M10 15.5 L20 12 L30 15.5 L30 20 L10 20 Z" fill="FG"/>' +
  '<path d="M10 20 Q11.7 18 13.3 20 Q15 18 16.7 20 Q18.3 18 20 20 Q21.7 18 23.3 20 Q25 18 26.7 20 Q28.3 18 30 20" fill="none" stroke="BG" stroke-width="1.2" opacity="0.55"/>' +
  '<line x1="12.5" y1="20" x2="12.5" y2="29" stroke="FG" stroke-width="2" stroke-linecap="round"/>' +
  '<line x1="27.5" y1="20" x2="27.5" y2="29" stroke="FG" stroke-width="2" stroke-linecap="round"/>';

// Restroom uses the paired-figure glyph (two-person silhouettes).
const PAIRED_FIGURE =
  '<g fill="FG">' +
  '<circle cx="14" cy="13" r="2.4"/>' +
  '<path d="M 10.5 17 L 17.5 17 L 16 25 L 16 30 L 12 30 L 12 25 Z"/>' +
  '<circle cx="26" cy="13" r="2.4"/>' +
  '<path d="M 22 17.5 L 30 17.5 L 27.6 24.5 L 27 30 L 25 30 L 25 24.5 L 23 30 L 21 30 L 22.4 24.5 Z"/>' +
  '</g>';

export const GLYPH_PATHS: Record<string, string> = {
  produce:
    // Woven market basket with arched handle and produce poking out the rim.
    // Original design had vertical "weave" strokes at width 0.8 opacity 0.35,
    // which render as sub-0.3px lines at our smallest used size (36px) and
    // disappear entirely. Kept the two horizontal weave bands at higher
    // opacity instead — they survive small renders and still convey "basket".
    // Untranslated, the bounding box is y=13..32 (midpoint 22.5); translating
    // up 2.5 lands it dead-center on the marker's y=20 axis.
    '<g transform="translate(0 -2.5)">' +
    '<path d="M10 20 L11 30 Q11 32 13 32 L27 32 Q29 32 29 30 L30 20 Z" fill="FG"/>' +
    '<path d="M10.5 24 L29.5 24" stroke="BG" stroke-width="1.4" opacity="0.55" stroke-linecap="round"/>' +
    '<path d="M11 28 L29 28" stroke="BG" stroke-width="1.4" opacity="0.55" stroke-linecap="round"/>' +
    '<rect x="9.5" y="19" width="21" height="2.5" rx="1.2" fill="FG"/>' +
    '<path d="M14 20 C14 13, 26 13, 26 20" fill="none" stroke="FG" stroke-width="2.2" stroke-linecap="round"/>' +
    '<path d="M17 19 C17 16, 15 14.5, 14.5 15.5 C15 17, 16 18, 17 19 Z" fill="FG"/>' +
    '<path d="M20 18.5 C20 15, 20 13.5, 20 15 C20 16.5, 20 17.5, 20 18.5 Z" fill="FG"/>' +
    '<path d="M23 19 C23 16, 25 14.5, 25.5 15.5 C25 17, 24 18, 23 19 Z" fill="FG"/></g>',
  baked_goods:
    // Domed loaf with scored top — clearer silhouette than a flat ellipse.
    '<rect x="10" y="22" width="20" height="8" rx="3" fill="FG"/>' +
    '<path d="M10 25 C10 17, 30 17, 30 25 Z" fill="FG"/>' +
    '<g stroke="BG" stroke-width="1.3" stroke-linecap="round" fill="none">' +
    '<path d="M15 22 Q16 19 17 22"/>' +
    '<path d="M19.5 21 Q20.5 17.5 21.5 21"/>' +
    '<path d="M24 22 Q25 19 26 22"/></g>',
  dairy_meat_eggs:
    // Egg silhouette with a soft offset highlight, 0.8 scale.
    '<g transform="matrix(0.8 0 0 0.8 4 4)">' +
    '<path d="M20 8 C14 8, 11 15, 11 21 C11 27, 15 32, 20 32 C25 32, 29 27, 29 21 C29 15, 26 8, 20 8 Z" fill="FG"/>' +
    '<ellipse cx="16" cy="16" rx="2.5" ry="3.5" fill="BG" opacity="0.25" transform="rotate(-20 16 16)"/></g>',
  flowers_plants:
    // wildflower with stem + leaf, 0.9 scale
    '<g transform="matrix(0.9 0 0 0.9 2 2)">' +
    '<path d="M 20 22 L 20 33" stroke="FG" stroke-width="1.5" stroke-linecap="round"/>' +
    '<path d="M 20 27 C 15 25, 13 27, 14 30 C 17 30, 19 29, 20 27 Z" fill="FG"/>' +
    '<g fill="FG">' +
    '<circle cx="20" cy="10" r="3.2"/>' +
    '<circle cx="26" cy="13" r="3.2"/>' +
    '<circle cx="14" cy="13" r="3.2"/>' +
    '<circle cx="24" cy="19" r="3.2"/>' +
    '<circle cx="16" cy="19" r="3.2"/></g>' +
    '<circle cx="20" cy="15" r="2" fill="BG"/></g>',
  prepared_foods:
    // Steaming bowl of ramen — fuller round bowl + chopsticks + steam wisps.
    '<path d="M10 22 Q10 31 20 31 Q30 31 30 22Z" fill="FG"/>' +
    '<rect x="10" y="21" width="20" height="2" rx="1" fill="FG"/>' +
    '<path d="M12 23.5 L28 23.5" stroke="BG" stroke-width="0.9" opacity="0.6"/>' +
    '<g stroke="FG" stroke-width="1.5" stroke-linecap="round">' +
    '<path d="M14 18 L18 22"/><path d="M17 16 L22 22"/></g>' +
    '<g fill="none" stroke="FG" stroke-width="1.3" stroke-linecap="round" opacity="0.7">' +
    '<path d="M22 17 C21 15,23 13,22 11"/>' +
    '<path d="M26 18 C25 16,27 14,26 12"/></g>',
  beverages:
    // Capped bottle with shoulder + neck and a horizontal label hint band,
    // 0.8 scale so the cap tab doesn't punch through the marker's top edge.
    '<g transform="matrix(0.8 0 0 0.8 4 4)">' +
    '<path d="M13 16 L14.5 32 Q14.5 33 16 33 L24 33 Q25.5 33 25.5 32 L27 16 Z" fill="FG"/>' +
    '<path d="M12 14 L12 16 Q12 17 13 17 L27 17 Q28 17 28 16 L28 14 Q28 13 27 13 L13 13 Q12 13 12 14 Z" fill="FG"/>' +
    '<path d="M17 13 Q17 11 20 11 Q23 11 23 13" fill="FG"/>' +
    '<rect x="21" y="5" width="2" height="9" rx="1" fill="FG"/>' +
    '<path d="M15 22 Q20 21 25 22" fill="none" stroke="BG" stroke-width="1" opacity="0.5" stroke-linecap="round"/></g>',
  specialty_foods:
    // Stacked / tiered specialty container — base body with banded lid tiers
    // and a soft label panel with a wavy line on the front, 0.8 scale.
    '<g transform="matrix(0.8 0 0 0.8 4 4)">' +
    '<path d="M11 18 L11 30 Q11 32 13 32 L27 32 Q29 32 29 30 L29 18 Z" fill="FG"/>' +
    '<rect x="13.5" y="14" width="13" height="5" rx="1" fill="FG"/>' +
    '<rect x="12.5" y="11.5" width="15" height="3.5" rx="1.7" fill="FG"/>' +
    '<rect x="14.5" y="9" width="11" height="3.5" rx="1.7" fill="FG"/>' +
    '<rect x="12" y="21" width="16" height="8" rx="1.5" fill="BG" opacity="0.4"/>' +
    '<path d="M12 25 Q15 23.5 20 25 Q25 26.5 28 25" fill="none" stroke="BG" stroke-width="0.9" opacity="0.5" stroke-linecap="round"/></g>',
  information:
    // filled disc + serif "i"
    '<circle cx="20" cy="20" r="10" fill="FG"/>' +
    '<path d="M 17.5 27 L 17.5 26 Q 18.2 25.8, 18.5 25 L 18.5 19.5 Q 18.2 18.7, 17.5 18.5 L 17.5 17.5 L 22.5 17.5 L 22.5 18.5 Q 21.8 18.7, 21.5 19.5 L 21.5 25 Q 21.8 25.8, 22.5 26 L 22.5 27 Z" fill="BG"/>' +
    '<circle cx="20" cy="14.8" r="1.6" fill="BG"/>',
  handmade:
    // classic crossed shears, 0.8 scale
    '<g transform="matrix(0.8 0 0 0.8 4 4)">' +
    '<path d="M 16.5 25 L 28 7 L 25.5 7 L 14.5 25 Z" fill="FG"/>' +
    '<path d="M 23.5 25 L 12 7 L 14.5 7 L 25.5 25 Z" fill="FG"/>' +
    '<circle cx="14" cy="27" r="3" fill="none" stroke="FG" stroke-width="1.8"/>' +
    '<circle cx="26" cy="27" r="3" fill="none" stroke="FG" stroke-width="1.8"/>' +
    '<circle cx="20" cy="16" r="0.9" fill="BG"/></g>',
  storefront:
    // Troy brownstone — cornice, upper windows, scalloped awning, door + storefront windows
    // y-scale is 0.88 (not 0.8) so the building reads portrait inside the rounded-square
    // marker; uniform 0.8 produced a landscape glyph (wider than tall) that looked squished.
    '<g transform="matrix(0.8 0 0 0.88 4 3.5)">' +
    '<rect x="6" y="7" width="28" height="2" fill="FG"/>' +
    '<rect x="7" y="9" width="26" height="24" fill="FG"/>' +
    '<rect x="10" y="11" width="5" height="6" fill="BG"/>' +
    '<rect x="25" y="11" width="5" height="6" fill="BG"/>' +
    '<path d="M 6 19 L 34 19 L 34 22 Q 31 23, 28 22.5 Q 25 23, 22 22.5 Q 19 23, 16 22.5 Q 13 23, 10 22.5 Q 7 23, 6 22 Z" fill="FG"/>' +
    '<g fill="BG">' +
    '<rect x="9" y="19" width="1.2" height="3"/>' +
    '<rect x="14" y="19" width="1.2" height="3"/>' +
    '<rect x="19" y="19" width="1.2" height="3"/>' +
    '<rect x="24" y="19" width="1.2" height="3"/>' +
    '<rect x="29" y="19" width="1.2" height="3"/></g>' +
    '<rect x="18" y="24" width="4" height="9" fill="BG"/>' +
    '<rect x="9" y="25" width="7" height="6" fill="BG"/>' +
    '<rect x="24" y="25" width="7" height="6" fill="BG"/></g>',
  tent: VENDOR_GLYPH,
  atm:
    // Card with magnetic stripe + "$" — reads as ATM at marker scale.
    '<rect x="9" y="13" width="22" height="14" rx="2" fill="FG"/>' +
    '<rect x="9" y="16" width="22" height="2.6" fill="BG"/>' +
    '<path d="M20 20.5 L20 25.5 M21.7 21.3 Q18.3 21.3 18.3 22.6 Q18.3 23.9 21.7 23.9 Q23.6 24.3 21.7 25.4 Q19 25.7 17.6 24.6" stroke="BG" stroke-width="1.1" stroke-linecap="round" fill="none"/>',
  first_aid:
    // Bold plus / medical cross — universal first-aid symbol.
    '<rect x="17" y="11" width="6" height="18" rx="1.2" fill="FG"/>' +
    '<rect x="11" y="17" width="18" height="6" rx="1.2" fill="FG"/>',
  restroom: PAIRED_FIGURE,
  stage:
    // Beamed double eighth-note (♫). Source art is a single path on a
    // 104.23×122.88 canvas; the matrix scales it to ~0.18 and recenters the
    // bounding box on the marker's (20,20) axis.
    '<g fill="FG" transform="matrix(0.18 0 0 0.18 10.6 8.9)">' +
    '<path d="M87.9,78.04c2.74-0.48,5.33-0.4,7.6,0.13V24.82L39.05,41.03v61.95c0.03,0.34,0.05,0.69,0.05,1.03c0,0,0,0.01,0,0.01c0,8.34-8.75,16.62-19.55,18.49C8.76,124.37,0,119.12,0,110.77c0-8.34,8.76-16.62,19.55-18.48c4.06-0.7,7.84-0.39,10.97,0.71l0-76.26h0.47L104.04,0v85.92c0.13,0.63,0.2,1.27,0.2,1.91c0,0,0,0,0,0.01c0,6.97-7.32,13.89-16.33,15.44c-9.02,1.56-16.33-2.83-16.33-9.8C71.57,86.51,78.88,79.59,87.9,78.04L87.9,78.04L87.9,78.04Z"/>' +
    '</g>',
};

// Fallback for unknown category IDs — minimal info-circle outline.
export const GLYPH_DEFAULT =
  '<g fill="none" stroke="FG" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
  '<circle cx="20" cy="20" r="7"/><path d="M20 19v5M20 16v0.5"/></g>';
